/**
 * Google Analytics integration with consent management
 * Only loads and tracks when user has given explicit consent
 */

import { hasAnalyticsConsent } from './consent'
import { config } from './config'

// Read GA measurement ID from config
const GA_MEASUREMENT_ID = config.googleAnalyticsMeasurementId

// Type definitions for Google Analytics gtag
type GtagCommand = 'config' | 'event' | 'js' | 'set'
type GtagEventParams = Record<string, unknown>

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (command: GtagCommand, ...args: unknown[]) => void
    [key: string]: unknown
  }
}

let isGALoaded = false

/**
 * Initialize Google Analytics with consent-gated loading
 * Only loads the GA script if user has given consent
 */
export function initializeAnalytics(): void {
  // Don't load if no measurement ID is configured
  if (!GA_MEASUREMENT_ID) {
    console.info('Google Analytics: No measurement ID configured')
    return
  }

  // Only load if user has given consent
  if (!hasAnalyticsConsent()) {
    console.info('Google Analytics: Waiting for user consent')
    return
  }

  // Don't load twice
  if (isGALoaded) {
    console.info('Google Analytics: Already loaded')
    return
  }

  try {
    // Load the gtag.js script dynamically
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
    document.head.appendChild(script)

    // Initialize dataLayer and gtag function
    window.dataLayer = window.dataLayer || []
    window.gtag = function gtag(command: GtagCommand, ...args: unknown[]) {
      window.dataLayer.push([command, ...args])
    }

    // Configure GA with the measurement ID
    window.gtag('js', new Date())
    window.gtag('config', GA_MEASUREMENT_ID, {
      anonymize_ip: true, // GDPR requirement
      cookie_flags: 'SameSite=None;Secure' // Modern cookie security
    })

    isGALoaded = true
    console.info('Google Analytics: Initialized with ID', GA_MEASUREMENT_ID)
  } catch (error) {
    console.error('Error initializing Google Analytics:', error)
  }
}

/**
 * Track a custom event (only if consent given and GA loaded)
 */
export function trackEvent(eventName: string, parameters?: GtagEventParams): void {
  if (!hasAnalyticsConsent() || !isGALoaded || !window.gtag) {
    return
  }

  try {
    window.gtag('event', eventName, parameters)
  } catch (error) {
    console.error('Error tracking event:', error)
  }
}

/**
 * Opt out of analytics (for CCPA compliance)
 * Disables GA tracking even if consent was previously given
 */
export function optOutAnalytics(): void {
  if (!GA_MEASUREMENT_ID || !window.gtag) {
    return
  }

  try {
    // Set GA opt-out property
    window[`ga-disable-${GA_MEASUREMENT_ID}`] = true
    console.info('Google Analytics: Opted out')
  } catch (error) {
    console.error('Error opting out of analytics:', error)
  }
}
